// AI-powered schedule suggestions and feedback integration.
//
// All LLM calls go through the server-side proxy at /api/ai-proxy, which holds
// the API key (in Function app settings) and validates the caller against the
// same Microsoft Graph / AAD security-group model as /api/drafts. The browser
// never sees the LLM key. We authenticate to the proxy with an MSAL Graph
// token, exactly like src/utils/draftStorage.js.

import { msalInstance } from '../authConfig';

const API_BASE = '/api/ai-proxy';

// Fully-qualified Graph scopes so MSAL never hands back a token issued for the
// app's own audience (which Graph would reject). Mirrors draftStorage.js.
const GRAPH_SCOPES = [
  'https://graph.microsoft.com/User.Read',
  'https://graph.microsoft.com/GroupMember.Read.All'
];

const getGraphToken = async () => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error('Not signed in. Please sign in to use the AI assistant.');
  }
  const request = { scopes: GRAPH_SCOPES, account };
  let result;
  try {
    result = await msalInstance.acquireTokenSilent(request);
  } catch (e) {
    try {
      result = await msalInstance.acquireTokenPopup(request);
    } catch (popupErr) {
      throw new Error(`Could not acquire Graph token: ${popupErr.message || popupErr}`);
    }
  }
  return result.accessToken;
};

class AIScheduleService {
  // The key lives server-side now, so the SPA can always attempt a call; the
  // proxy reports 503 if it isn't configured. Kept as a getter so existing
  // UI gating (buttons, warnings) continues to work without code changes.
  get isConfigured() {
    return true;
  }

  async callAI(messages, maxTokens = 2000) {
    let token;
    try {
      token = await getGraphToken();
    } catch (e) {
      return { success: false, error: e.message };
    }

    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-graph-access-token': token,
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ messages, maxTokens })
      });

      if (!response.ok) {
        let message = `AI request failed (${response.status})`;
        try {
          const errBody = await response.json();
          if (errBody?.message) message = errBody.message;
        } catch (_) { /* non-JSON error body */ }
        throw new Error(message);
      }

      const data = await response.json();
      return { success: true, data: data.content };
    } catch (error) {
      console.error('AI service error:', error);
      return { success: false, error: error.message };
    }
  }

  // Generate scheduling suggestions based on analysis
  async getSuggestions(analyticsSummary, courseProfiles, targetYear) {
    const topCourses = courseProfiles.slice(0, 30).map(c => ({
      name: c.name,
      avgFrequency: c.avgFrequencyPerYear,
      suggestedFrequency: c.suggestedFrequency,
      avgFillRate: Math.round(c.avgFillRate * 100) + '%',
      trend: c.trend,
      typicalMonths: c.monthlyDistribution
        .map((count, month) => ({ month: monthName(month), count }))
        .filter(m => m.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 4)
        .map(m => m.month)
        .join(', '),
      typicalCapacity: c.typicalCapacity,
      typicalDuration: c.typicalDuration + ' days'
    }));

    const systemPrompt = `You are a training schedule optimization expert for a maritime training institute. 
You help managers create efficient annual training schedules based on historical data.
Be specific and actionable. Reference course names directly.
Format your response with clear sections using markdown headers.`;

    const userPrompt = `Analyze this historical training data and provide scheduling recommendations for ${targetYear}.

SUMMARY:
- ${analyticsSummary.totalCourses} unique courses analyzed
- ${analyticsSummary.totalHistoricalSessions} sessions over ${analyticsSummary.yearsAnalyzed} years (${analyticsSummary.yearRange})
- Average fill rate: ${Math.round(analyticsSummary.avgFillRate * 100)}%
- Under-utilized courses (<50% fill): ${analyticsSummary.utilizationBreakdown.underUtilized}
- Well-utilized (50-90% fill): ${analyticsSummary.utilizationBreakdown.wellUtilized}
- Over-subscribed (>90% fill): ${analyticsSummary.utilizationBreakdown.overSubscribed}
- Trending up: ${analyticsSummary.trendingUp} courses, Trending down: ${analyticsSummary.trendingDown} courses

TOP COURSES:
${JSON.stringify(topCourses, null, 2)}

MONTHLY VOLUME DISTRIBUTION (all courses):
${analyticsSummary.monthlyVolume.map((v, i) => `${monthName(i)}: ${v} sessions`).join('\n')}

Please provide:
1. **Key Observations** — Patterns and insights from the data
2. **Frequency Adjustments** — Specific courses that should run more or less often, and why
3. **Capacity Recommendations** — Courses where seat count should be adjusted
4. **Seasonal Optimization** — Suggestions for better month/timing distribution
5. **Risk Flags** — Courses at risk of cancellation or over-subscription`;

    return this.callAI([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ], 3000);
  }

  // Process manager feedback and refine suggestions
  async refineSuggestions(currentDraft, managerFeedback, courseProfiles) {
    const draftSummary = currentDraft.slice(0, 50).map(s => ({
      name: s.name,
      location: s.locationName || '',
      date: new Date(s.startDate).toLocaleDateString('en-GB'),
      capacity: s.maxCapacity,
      confidence: s.confidence + '%'
    }));

    const systemPrompt = `You are a training schedule optimization expert for a maritime training institute.
A draft schedule has been generated and a manager has provided feedback.
Suggest specific changes to the draft based on their feedback.
Return a JSON array of adjustments in this format:
[{"courseName": "...", "action": "move|add|remove|resize", "details": "..."}]
Wrap the JSON in \`\`\`json code blocks. Add a brief explanation before the JSON.`;

    const userPrompt = `CURRENT DRAFT (first 50 sessions):
${JSON.stringify(draftSummary, null, 2)}

MANAGER FEEDBACK:
"${managerFeedback}"

Suggest specific adjustments to the draft schedule based on this feedback.`;

    return this.callAI([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ], 2000);
  }

  // Review manager planning rules against the generated draft. This is
  // deliberately read-only: deterministic rules are enforced by the generator,
  // while the AI explains conflicts and advisory notes without pretending its
  // prose changed the schedule.
  async reviewPlanningRules(currentDraft, managerRules, targetYear) {
    const draftSummary = currentDraft.map(s => ({
      course: s.name,
      location: s.locationName || '',
      startDate: String(s.startDate || '').slice(0, 10),
      endDate: String(s.endDate || '').slice(0, 10)
    }));

    const systemPrompt = `You review annual maritime training schedules against manager-defined rules.
This is a READ-ONLY review: do not claim to apply, remove, move, or edit sessions.
Use the explicit location field only. Never infer that one city is another city because it is nearby or appears in a course name.
Report exact violations with the course, location, and date. If a rule is satisfied, say so clearly.
Use concise Markdown, not JSON.`;

    const userPrompt = `TARGET YEAR: ${targetYear}

MANAGER RULES AND NOTES:
${managerRules}

GENERATED DRAFT (${draftSummary.length} sessions):
${JSON.stringify(draftSummary, null, 2)}

Review the draft against the rules. Distinguish deterministic enforced rules from advisory notes.`;

    return this.callAI([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ], 3000);
  }

  // Analyze a specific course for deeper insights
  async analyzeCourse(courseName, courseProfile) {
    const systemPrompt = `You are a training schedule expert. Provide a brief, focused analysis of this course's scheduling patterns. Be concise (max 150 words).`;

    const userPrompt = `Course: "${courseName}"
- Sessions: ${courseProfile.totalSessions} total
- Avg frequency: ${courseProfile.avgFrequencyPerYear}/year
- Avg fill rate: ${Math.round(courseProfile.avgFillRate * 100)}%
- Trend: ${courseProfile.trend}
- Typical capacity: ${courseProfile.typicalCapacity}
- Typical duration: ${courseProfile.typicalDuration} days
- Usually runs in: ${courseProfile.monthlyDistribution
      .map((count, month) => ({ month: monthName(month), count }))
      .filter(m => m.count > 0)
      .sort((a, b) => b.count - a.count)
      .map(m => m.month)
      .join(', ')}
- Year-by-year frequency: ${JSON.stringify(courseProfile.yearFrequencies)}

Provide scheduling recommendation for next year.`;

    return this.callAI([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ], 500);
  }
}

const monthName = (idx) =>
  ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][idx];

const aiScheduleService = new AIScheduleService();
export default aiScheduleService;
